/**
 * Config Metadata Utilities
 * Merge device configuration with master parameter schema metadata
 */

import masterParamsSchema from '../reference/masterParamsSchema.json';

/**
 * Merge device config with metadata from master schema
 * @param {Object} deviceConfig - Parsed device config (flat key-value pairs)
 * @returns {Array} Array of { param, value, meta } objects
 * 
 * Example:
 * Input: { "sip.proxy": "203.0.113.1", "sip.detect.mode": "1" }
 * Output: [
 *   {
 *     param: "sip.proxy",
 *     value: "203.0.113.1",
 *     meta: { type: "ip", label: "SIP Domain", ... }
 *   },
 *   ...
 * ]
 */
export function mergeConfigWithMetadata(deviceConfig) {
  const merged = [];

  // Iterate through all parameters in device config
  for (const [param, value] of Object.entries(deviceConfig)) {
    const meta = masterParamsSchema[param] || null;
    
    merged.push({
      param,
      value,
      meta
    });
  }

  return merged;
}

/**
 * Group parameters by section and group from metadata
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @returns {Object} Nested structure: { section: { group: [params] } }
 * 
 * Example:
 * {
 *   "Basic Settings": {
 *     "SIP": [
 *       { param: "sip.proxy", value: "...", meta: {...} },
 *       { param: "sip.detect.mode", value: "...", meta: {...} }
 *     ],
 *     "Features": [...]
 *   },
 *   "Additional Features": {...}
 * }
 */
export function groupByMetadata(mergedParams) {
  const grouped = {};

  for (const item of mergedParams) {
    const section = item.meta?.section || 'Uncategorized';
    const group = item.meta?.group || 'General';

    if (!grouped[section]) {
      grouped[section] = {};
    }

    if (!grouped[section][group]) {
      grouped[section][group] = [];
    }

    grouped[section][group].push(item);
  }

  return grouped;
}

/**
 * Flatten device config from section-based structure to flat key-value
 * @param {Object} sections - Config in section format { admin: { devname: "..." }, sip: { proxy: "..." } }
 * @returns {Object} Flat config { "admin.devname": "...", "sip.proxy": "..." }
 */
export function flattenConfig(sections) {
  const flat = {};

  for (const [section, params] of Object.entries(sections)) {
    for (const [key, value] of Object.entries(params)) {
      const fullKey = section === 'general' ? key : `${section}.${key}`;
      flat[fullKey] = value;
    }
  }

  return flat;
}

/**
 * Check if a parameter should be visible based on requirements
 * @param {Object} meta - Parameter metadata
 * @param {Object} flatConfig - Flat device config
 * @returns {boolean} True if parameter should be visible
 */
export function isParameterVisible(meta, flatConfig) {
  if (!meta?.requirements || meta.requirements === 'NA' || meta.requirements === 'N/A' || meta.requirements === '') {
    return true;
  }

  // Parse requirement: "sip.detect.mode = 1" or "audio.page.mode = 2"
  const requirement = meta.requirements.trim();
  if (!requirement.includes('=')) {
    return true;
  }
  const match = requirement.match(/^(.+?)\s*=\s*["']?(.+?)["']?(?:\s+and\s+.*)?$/);
  
  if (!match) {
    console.warn(`[configMetadataUtils] Unable to parse requirement: ${requirement}`);
    return true; // Show by default if can't parse
  }

  const [, reqParam, reqValue] = match;
  const currentValue = flatConfig?.[reqParam];
  
  // Compare values (handle both string and number comparisons)
  return String(currentValue) === String(reqValue);
}

/**
 * Filter parameters by search query
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @param {string} query - Search query
 * @returns {Array} Filtered parameters
 */
export function filterParameters(mergedParams, query) {
  if (!query || !query.trim()) {
    return mergedParams;
  }

  const lowerQuery = query.toLowerCase().trim();

  return mergedParams.filter(item => {
    const matchesParam = item.param.toLowerCase().includes(lowerQuery);
    const matchesLabel = item.meta?.label?.toLowerCase().includes(lowerQuery);
    const matchesDescription = item.meta?.description?.toLowerCase().includes(lowerQuery);
    
    // Search parameter name, label, and description (NOT value)
    return matchesParam || matchesLabel || matchesDescription;
  });
}

/**
 * Filter parameters by value search query
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @param {string} query - Search query
 * @returns {Array} Filtered parameters
 */
export function filterParametersByValue(mergedParams, query) {
  if (!query || !query.trim()) {
    return mergedParams;
  }

  const lowerQuery = query.toLowerCase().trim();

  return mergedParams.filter(item => {
    const matchesValue = item.value?.toString().toLowerCase().includes(lowerQuery);
    return matchesValue;
  });
}

/**
 * Get all unique sections from merged params
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @returns {Array} Array of unique section names
 */
export function getSections(mergedParams) {
  const sections = new Set();
  
  for (const item of mergedParams) {
    const section = item.meta?.section || 'Uncategorized';
    sections.add(section);
  }

  return Array.from(sections).sort();
}

/**
 * Get all unique groups within a section
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @param {string} section - Section name
 * @returns {Array} Array of unique group names
 */
export function getGroupsInSection(mergedParams, section) {
  const groups = new Set();
  
  for (const item of mergedParams) {
    if ((item.meta?.section || 'Uncategorized') === section) {
      const group = item.meta?.group || 'General';
      groups.add(group);
    }
  }

  return Array.from(groups).sort();
}

/**
 * Get parameters in a specific section and group
 * @param {Array} mergedParams - Array of { param, value, meta }
 * @param {string} section - Section name
 * @param {string} group - Group name
 * @returns {Array} Filtered parameters
 */
export function getParametersInGroup(mergedParams, section, group) {
  return mergedParams.filter(item => {
    const itemSection = item.meta?.section || 'Uncategorized';
    const itemGroup = item.meta?.group || 'General';
    return itemSection === section && itemGroup === group;
  });
}
